import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, Star, UtensilsCrossed, Store, Trophy, Building2,
  Share2, MapPin, Globe, MonitorPlay, Palette, Clapperboard, Ticket, Wallet, Mail, Receipt, Cloud, ChevronDown, Smartphone, CheckCheck, Server, Music2 } from "lucide-react";
import { SpotLightbox } from "@/components/RobbieSpot";
import { DemoButton } from "@/components/DemoButton";  // eslint-disable-line no-unused-vars
import { KanalLogoRow } from "@/components/KanalLogos";

/* Neue Startseite (Thomas, Entwurf entwurf_startseite_desktop_handy.png):
   Hero (2 Spalten, Robbie + WhatsApp-Chat) → SO GEHT'S → Branchen-Weiche → Preis-Balken.
   Danach bleiben in Home.jsx: Beispiele (Videos), Referenzen/Zahlen + Kundenstimmen, FAQ, Abschluss. */

const H = { fontFamily: "'Outfit', 'Poppins', sans-serif", fontWeight: 800 };
const B = { fontFamily: "'Poppins', sans-serif" };
const CREME = "#FAF8F3";

const ROBBIE = "/img/mascots/robbie-winkt.webp";
const FOTO_ROH = "/img/chat-demo/kuerbissuppe-roh.webp";
const POST = "/img/beispiele/gastro-post.webp";

const up = (d = 0) => ({ initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.55, delay: d, ease: [0.22, 1, 0.36, 1] } });

const usePrefersReducedMotion = () => {
  const [reduce, setReduce] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
  useEffect(() => {
    const m = window.matchMedia("(prefers-reduced-motion: reduce)");
    const on = () => setReduce(m.matches);
    on(); m.addEventListener("change", on);
    return () => m.removeEventListener("change", on);
  }, []);
  return reduce;
};

/* Nur EIN Hero-Baum rendern (verhindert doppeltes <h1> & zwei Chat-Intervalle) */
const useIsDesktop = () => {
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches
  );
  useEffect(() => {
    const m = window.matchMedia("(min-width: 1024px)");
    const on = () => setIsDesktop(m.matches);
    on(); m.addEventListener("change", on);
    return () => m.removeEventListener("change", on);
  }, []);
  return isDesktop;
};

/* CSS scroll-driven animations verfügbar? (Chrome/Edge). Safari/Firefox → IntersectionObserver-Fallback. */
const SUPPORTS_SDA = typeof window !== "undefined" && window.CSS && typeof CSS.supports === "function" && CSS.supports("animation-timeline: view()");

/* Fallback: blendet .sd-reveal-Elemente per IO ein, wenn scroll-driven nicht unterstützt wird. */
const useRevealFallback = (rootRef) => {
  useEffect(() => {
    if (SUPPORTS_SDA) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const root = rootRef.current;
    if (!root) return;
    const items = root.querySelectorAll(".sd-reveal");
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add("is-in"); io.unobserve(e.target); } });
    }, { threshold: 0.15 });
    items.forEach((i) => io.observe(i));
    return () => io.disconnect();
  }, [rootRef]);
};

/* Robbie neigt sich sanft Richtung Mauszeiger – nur Desktop mit Maus, gedämpft per rAF. */
const useRobbieTilt = () => {
  const ref = useRef(null);
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!window.matchMedia("(pointer: fine)").matches) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const el = ref.current;
    if (!el) return;
    let raf = 0, curX = 0, curY = 0, tgX = 0, tgY = 0;
    const clamp = (v) => Math.max(-8, Math.min(8, v));
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      const cx = r.left + r.width / 2, cy = r.top + r.height / 2;
      tgY = clamp(((e.clientX - cx) / (window.innerWidth / 2)) * 8);
      tgX = clamp((-(e.clientY - cy) / (window.innerHeight / 2)) * 8);
    };
    const onLeave = () => { tgX = 0; tgY = 0; };
    const loop = () => {
      curX += (tgX - curX) * 0.08;
      curY += (tgY - curY) * 0.08;
      const tx = (curY / 8) * 4, ty = (-curX / 8) * 4;
      el.style.transform = `perspective(700px) rotateX(${curX.toFixed(2)}deg) rotateY(${curY.toFixed(2)}deg) translate3d(${tx.toFixed(1)}px, ${ty.toFixed(1)}px, 0)`;
      raf = requestAnimationFrame(loop);
    };
    window.addEventListener("mousemove", onMove, { passive: true });
    document.addEventListener("mouseleave", onLeave);
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("mousemove", onMove); document.removeEventListener("mouseleave", onLeave); };
  }, []);
  return ref;
};

/* ---------- Beweis-Zahl zählt hoch, wenn sichtbar ---------- */
const CountUp = ({ to, decimals = 0, grupp = true, className, style }) =>
  // Zahl steht IMMER auf dem Endwert – in SSR, Hydration UND zur Laufzeit. Kein numerisches
  // Hochzählen mehr, damit Suchmaschinen/KI-Crawler nie einen Zwischenwert (z. B. „0,8"/„1983")
  // im gerenderten DOM abgreifen können. Die dezente Einblendung macht der umgebende Motion-Block.
  <span className={className} style={style}>{to.toLocaleString("de-DE", { minimumFractionDigits: decimals, maximumFractionDigits: decimals, useGrouping: grupp })}</span>;

/* ---------- Animierter WhatsApp-Chat ---------- */
const WaCheck = () => <Check size={13} strokeWidth={3} className="shrink-0 text-[#25D366]" aria-hidden="true" />;
const Ticks = ({ blau }) => <CheckCheck size={14} strokeWidth={2.5} className={`inline-block align-[-2px] transition-colors duration-500 ${blau ? "text-[#34B7F1]" : "text-[#8696A0]"}`} aria-hidden="true" />;

const ChatBubble = ({ side, children, testId }) => (
  <motion.div initial={{ opacity: 0, y: 10, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
    className={`max-w-[85%] rounded-2xl px-3 py-2 text-[12.5px] leading-snug shadow-sm ${side === "right" ? "self-end rounded-br-md bg-[#DCF8C6] text-[#111]" : "self-start rounded-bl-md bg-white text-[#111]"}`} style={B} data-testid={testId}>
    {children}
  </motion.div>
);

const Typing = () => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="self-start flex items-center gap-1 rounded-2xl rounded-bl-md bg-white px-3 py-2.5 shadow-sm" aria-label="Robbie tippt">
    {[0, 1, 2].map((i) => (
      <motion.span key={i} className="h-1.5 w-1.5 rounded-full bg-[#9A958B]" animate={{ opacity: [0.3, 1, 0.3], y: [0, -2, 0] }} transition={{ duration: 0.9, repeat: Infinity, delay: i * 0.15 }} />
    ))}
  </motion.div>
);

// eslint-disable-next-line no-unused-vars
const PhoneChat = ({ testId = "hero-chat" }) => {
  const reduce = usePrefersReducedMotion();
  const [phase, setPhase] = useState(reduce ? 9 : 0);
  const [checks, setChecks] = useState(reduce ? 3 : 0);
  const scrollRef = useRef(null);
  const rootRef = useRef(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: reduce ? "auto" : "smooth" });
  }, [phase, checks, reduce]);

  useEffect(() => {
    if (reduce) { setPhase(9); setChecks(3); return; }
    const node = rootRef.current;
    if (!node) return;
    const timers = [];
    let playing = false;
    const clear = () => { timers.forEach(clearTimeout); timers.length = 0; };
    const run = () => {
      clear();
      playing = true;
      setPhase(0); setChecks(0);
      const steps = [
        [300, () => setPhase(1)],   // Kunde: Foto
        [1000, () => setPhase(2)],  // Robbie tippt
        [1900, () => setPhase(3)],  // Robbie: Post-Vorschau
        [2900, () => setPhase(4)],  // Kunde: Freigeben ✓
        [3500, () => setPhase(5)],  // Robbie tippt
        [4100, () => { setPhase(6); setChecks(0); }], // Ist live:
        [4400, () => setChecks(1)],
        [4700, () => setChecks(2)],
        [5000, () => { setChecks(3); playing = false; }], // Endzustand – stoppt
      ];
      steps.forEach(([d, fn]) => timers.push(setTimeout(fn, d)));
    };
    const io = new IntersectionObserver((entries) => {
      const e = entries[0];
      if (e.isIntersecting) { if (!playing) run(); }
      else { clear(); playing = false; } // erlaubt Replay beim Wieder-ins-Bild-Scrollen
    }, { threshold: 0.35 });
    io.observe(node);
    return () => { io.disconnect(); clear(); };
  }, [reduce]);

  const show = (n) => phase >= n;

  return (
    <div ref={rootRef} className="relative mx-auto w-full max-w-[320px]">
      {/* Handy-Rahmen */}
      <div className="relative rounded-[2.4rem] border-[3px] border-[#111] bg-[#111] p-2 shadow-[10px_14px_0_0_rgba(17,17,17,0.9)]">
        <div className="overflow-hidden rounded-[1.9rem] bg-[#EFE7DE]">
          {/* WA-Kopf */}
          <div className="flex items-center gap-2.5 bg-[#075E54] px-3 py-2.5">
            <img src={ROBBIE} alt="Robbie" width="36" height="36" className="h-9 w-9 rounded-full bg-[#FFD60A] object-cover" />
            <div className="min-w-0">
              <p style={{ ...B, fontWeight: 600 }} className="truncate text-[13px] text-white">Robbie · DIMEDIAL</p>
              <p style={B} className="text-[10.5px] text-white/70">online</p>
            </div>
          </div>
          {/* Verlauf */}
          <div ref={scrollRef} className="flex h-[290px] flex-col gap-2 overflow-hidden px-3 py-3.5" data-testid={testId}>
            {show(1) && (
              <ChatBubble side="right" testId="hero-chat-kunde-foto">
                <span className="mb-1.5 block overflow-hidden rounded-xl">
                  <img src={FOTO_ROH} alt="Foto der Kürbissuppe vom Kunden" width="280" height="200" loading="lazy" className="h-28 w-full object-cover" />
                </span>
                Heute Kürbissuppe, 8,90 €
                <span className="mt-1 block text-right text-[10px] text-[#667781]">09:41 <Ticks blau={phase >= 3} /></span>
              </ChatBubble>
            )}
            {phase === 2 && <Typing />}
            {show(3) && (
              <ChatBubble side="left" testId="hero-chat-robbie-post">
                Fertig! Hier ist dein Post im Hauslook – plus Google-Meldung und Bildschirm-Kachel. Freigeben?
                <span className="mt-2 block overflow-hidden rounded-xl border border-black/5">
                  <img src={POST} alt="Fertiger Instagram-Post der Kürbissuppe im Hauslook" width="280" height="210" loading="lazy" className="w-full object-cover" />
                </span>
                <span className="mt-2 flex gap-1.5">
                  <span className="flex-1 rounded-lg bg-[#25D366] py-1.5 text-center text-[11.5px] font-bold text-white">Freigeben</span>
                  <span className="flex-1 rounded-lg border border-black/15 py-1.5 text-center text-[11.5px] font-bold text-[#111]">Ändern</span>
                </span>
              </ChatBubble>
            )}
            {show(4) && (
              <ChatBubble side="right" testId="hero-chat-kunde-freigeben">
                Freigeben <Check size={12} strokeWidth={3} className="inline text-[#075E54]" />
                <span className="mt-1 block text-right text-[10px] text-[#667781]">09:41 <Ticks blau={phase >= 5} /></span>
              </ChatBubble>
            )}
            {phase === 5 && <Typing />}
            {show(6) && (
              <ChatBubble side="left" testId="hero-chat-robbie-live">
                <span className="font-semibold">Ist live:</span>
                <span className="mt-1.5 flex flex-col gap-1">
                  {checks >= 1 && <motion.span initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} className="flex items-center gap-1.5"><WaCheck /> Instagram &amp; Facebook</motion.span>}
                  {checks >= 2 && <motion.span initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} className="flex items-center gap-1.5"><WaCheck /> Google-Profil</motion.span>}
                  {checks >= 3 && <motion.span initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} className="flex items-center gap-1.5"><WaCheck /> Bildschirm im Gastraum</motion.span>}
                </span>
              </ChatBubble>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ---------- Robbie + Sprechblase ---------- */
// eslint-disable-next-line no-unused-vars
const RobbieFigur = ({ className = "relative", size = "w-44", testId = "hero-robbie" }) => {
  const reduce = usePrefersReducedMotion();
  const tiltRef = useRobbieTilt();
  return (
    <div className={className} data-testid={testId}>
      <motion.div
        initial={reduce ? false : { opacity: 0, scale: 0.8, y: 6 }}
        animate={reduce ? {} : { opacity: 1, scale: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="absolute -top-6 left-1/2 z-20 w-max -translate-x-1/2 rounded-2xl border-2 border-[#111] bg-[#FFD60A] px-3 py-2 shadow-[3px_3px_0_0_#111]">
        <p style={{ ...B, fontWeight: 600 }} className="text-[12px] leading-tight text-[#111]">Hi, ich bin Robbie.<br />Ich mach das für dich.</p>
        <span className="absolute -bottom-2 left-8 h-3 w-3 rotate-45 border-b-2 border-r-2 border-[#111] bg-[#FFD60A]" aria-hidden="true" />
      </motion.div>
      <div ref={tiltRef} style={{ willChange: "transform" }}>
        <motion.img
          src={ROBBIE} alt="Robbie, der digitale Mitarbeiter, winkt" width="320" height="320"
          className={`${size} drop-shadow-[0_18px_30px_rgba(0,0,0,0.18)]`}
          animate={reduce ? {} : { y: [0, -8, 0], rotate: [0, -1.5, 0] }}
          transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }} />
      </div>
    </div>
  );
};

/* ---------- Hero-Textbausteine ---------- */
const HeroBadge = () => (
  <span style={{ ...B, fontWeight: 600 }} className="inline-flex items-center gap-2 rounded-full border-2 border-[#111] bg-white px-3.5 py-1.5 text-[11.5px] font-bold text-[#111] shadow-[3px_3px_0_0_#111]" data-testid="hero-badge">
    <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-[#34A853]" aria-hidden="true" />Für Gastro, Handel, Vereine &amp; kleine Firmen
  </span>
);

const HeroHeadline = () => (
  <h1 style={H} className="text-[#111] leading-[1.03] tracking-[-0.015em] text-[8vw] sm:text-5xl lg:text-[clamp(2.4rem,3.4vw,3.5rem)]" data-testid="hero-headline">
    Du kümmerst dich um deine Kunden. <span className="marker-draw box-decoration-clone px-1.5">Robbie um den Rest.</span>
  </h1>
);

const HeroLead = () => (
  <p style={B} className="text-[15px] md:text-base text-[#4A4A4A] leading-relaxed max-w-[46ch]" data-testid="hero-lead">
    Ein Foto per WhatsApp – Robbie macht Posts, Google-Beiträge und mehr daraus. Du tippst nur „Freigeben“.
  </p>
);

const scrollToPrinzip = (e) => {
  e.preventDefault();
  const el = document.getElementById("prinzip");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const HeroButtons = ({ onDemo }) => (
  <div className="flex flex-col gap-4">
    <Link to="/kunden/registrieren" data-testid="hero-cta-test" style={{ ...B, fontWeight: 700 }}
      className="inline-flex w-fit items-center justify-center gap-2 whitespace-nowrap rounded-full border-2 border-[#111] bg-[#FFD60A] px-6 py-3.5 text-[15px] font-bold text-[#111] shadow-[0_6px_0_0_#111] transition-transform hover:-translate-y-0.5 active:translate-y-0 active:shadow-[0_3px_0_0_#111]">
      30 Tage kostenlos testen <ArrowRight size={18} strokeWidth={2.5} />
    </Link>
    <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
      <a href="#prinzip" onClick={scrollToPrinzip} data-testid="hero-cta-prinzip" style={{ ...B, fontWeight: 600 }}
        className="group inline-flex items-center gap-1.5 text-[15px] font-semibold text-[#111] underline decoration-[#FDD60A] decoration-2 underline-offset-4 transition-colors hover:decoration-[#111]">
        So funktioniert's <ChevronDown size={16} strokeWidth={2.5} className="transition-transform group-hover:translate-y-0.5" />
      </a>
      <button type="button" data-testid="hero-cta-demo" onClick={() => { try { fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stats/demo-cta`, { method: "POST", headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" }, body: JSON.stringify({ quelle: "startseite-hero" }) }); } catch { /* egal */ } onDemo?.(); }} style={{ ...B, fontWeight: 600 }}
        className="group inline-flex items-center gap-1.5 text-[15px] font-semibold text-[#111] underline decoration-[#FDD60A] decoration-2 underline-offset-4 transition-colors hover:decoration-[#111]">
        Demo ansehen <ArrowRight size={16} strokeWidth={2.5} className="transition-transform group-hover:translate-x-0.5" />
      </button>
    </div>
  </div>
);

const HeroNote = () => (
  <div className="flex flex-col gap-1">
    <p style={B} className="text-[14px] text-[#6B6760]" data-testid="hero-note">Ohne Zahlungsdaten. Endet automatisch – nichts verlängert sich.</p>
    <p style={B} className="text-[13px] text-[#6B6760]" data-testid="hero-note-wa">Lieber erst fragen? <a href="https://wa.me/4952211412070?text=Hallo%20Robbie" target="_blank" rel="noreferrer" className="font-semibold text-[#111] underline underline-offset-2">WhatsApp an Robbie: 05221 1412070</a></p>
  </div>
);

const PROOF = [
  { id: "google", zahl: <span className="inline-flex items-center gap-1"><CountUp to={5} decimals={1} /> <Star size={15} fill="#FFD60A" className="text-[#FFD60A]" /></span>, text: "bei Google" },
  { id: "reach", zahl: <>250.000+</>, text: "Instagram-Aufrufe für Basketball Herford in 90 Tagen" },
  { id: "seit", zahl: <>seit <CountUp to={2000} grupp={false} /></>, text: "in Herford" },
];

const ProofTiles = () => (
  <div className="overflow-hidden rounded-2xl border-2 border-[#111] bg-white shadow-[3px_3px_0_0_#111]" data-testid="hero-proof">
    <ul className="grid grid-cols-3 divide-x-2 divide-[#111]/10">
      {PROOF.map((p) => (
        <li key={p.id} className="flex flex-col items-center justify-start px-2.5 py-3.5 text-center" data-testid={`hero-proof-${p.id}`}>
          <p style={H} className="flex items-center justify-center gap-1 text-[17px] leading-none text-[#111]">{p.zahl}</p>
          <p style={B} className="mt-1.5 text-[10.5px] leading-snug text-[#6B6760]">{p.text}</p>
        </li>
      ))}
    </ul>
  </div>
);

/* ---------- Hero-Handy: stumme Endlosschleife des Robbie-Imagefilms ---------- */
const HERO_LOOP_MP4 = "/media/robbie/hero-loop.mp4";
const HERO_LOOP_WEBM = "/media/robbie/hero-loop.webm";
const HERO_LOOP_POSTER = "/media/robbie/hero-loop-poster.webp";
const HERO_TOASTS = ["+38 Likes", "Google-Beitrag live", "Tisch für 4 reserviert"];

const HeroStoryPhone = ({ testId = "hero-story", mobile = false }) => {
  const reduce = usePrefersReducedMotion();
  // Video erst nach dem ersten Paint laden, damit das WebP-Poster LCP bleibt.
  const [mountVideo, setMountVideo] = useState(false);
  useEffect(() => {
    if (reduce) return undefined;
    const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 200));
    const cancel = window.cancelIdleCallback || clearTimeout;
    const id = idle(() => setMountVideo(true));
    return () => cancel(id);
  }, [reduce]);

  const phoneSize = mobile ? "h-[460px] w-[258px]" : "h-[600px] w-[340px]";
  const tilt = !mobile && !reduce ? { transform: "rotate(-3deg)" } : undefined;
  const maxToasts = mobile ? 2 : 3;
  const toasts = HERO_TOASTS.slice(0, maxToasts);

  return (
    <div className="relative flex flex-col items-center gap-5" data-testid={testId}>
      <div className="pointer-events-none absolute left-1/2 top-[44%] -z-0 h-[78%] w-[112%] -translate-x-1/2 -translate-y-1/2 rounded-[46%] bg-[#FDD105]/45 blur-3xl" aria-hidden="true" />
      <div className="relative" style={tilt}>
        <div className={`relative ${phoneSize} overflow-hidden rounded-[2.4rem] border-[6px] border-[#111] bg-[#111] shadow-[12px_12px_0_0_#111]`}>
          <span style={{ ...B, fontWeight: 700 }} className="absolute right-3 top-6 z-30 inline-flex items-center gap-1 rounded-full bg-[#34A853] px-2 py-0.5 text-[10px] font-bold text-white shadow-[0_2px_6px_rgba(0,0,0,0.3)]" data-testid="hero-story-badge">
            <Check size={11} strokeWidth={3} aria-hidden="true" /> Freigegeben
          </span>
          {/* Poster-Standbild (WebP) – bleibt LCP, Video legt sich danach darüber */}
          <img src={HERO_LOOP_POSTER} alt="Robbie macht Social Media, Google und Bildschirm aus einem Foto" width="340" height="600" fetchPriority="high" decoding="async" className="absolute inset-0 h-full w-full object-cover" data-testid="hero-story-poster" />
          {!reduce && mountVideo && (
            <video poster={HERO_LOOP_POSTER} muted autoPlay loop playsInline preload="auto"
              className="absolute inset-0 h-full w-full object-cover" data-testid="hero-story-video" aria-hidden="true">
              <source src={HERO_LOOP_WEBM} type="video/webm" />
              <source src={HERO_LOOP_MP4} type="video/mp4" />
            </video>
          )}
        </div>
        {/* Erfolgsmomente – Beispielwerte, keine Live-Statistik */}
        <div className={`absolute z-40 flex flex-col gap-2 ${mobile ? "bottom-10 -left-3" : "bottom-16 -left-10"}`} data-testid="hero-story-toasts" aria-hidden="true">
          {toasts.map((t, i) => (
            <motion.div key={t}
              initial={reduce ? false : { opacity: 0, x: -18, scale: 0.9 }}
              animate={reduce ? undefined : { opacity: 1, x: 0, scale: 1 }}
              transition={reduce ? undefined : { delay: 0.5 + i * 0.5, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              style={{ ...B, fontWeight: 600 }}
              className={`inline-flex items-center gap-1.5 self-start rounded-xl border-2 border-[#111] bg-white shadow-[4px_4px_0_0_#111] ${mobile ? "px-2.5 py-1 text-[11px]" : "px-3 py-1.5 text-[12.5px]"} text-[#111]`}>
              <span className="grid h-4 w-4 shrink-0 place-items-center rounded-full bg-[#34A853] text-white"><Check size={10} strokeWidth={3} /></span>
              {t}
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ---------- 1 · HERO ---------- */
export const HeroNeu = () => {
  const [video, setVideo] = useState(false);
  const vBtn = useRef(null);
  const isDesktop = useIsDesktop();
  return (
    <section className="relative overflow-hidden" style={{ background: CREME }} data-testid="hero-neu">
      <SpotLightbox open={video} onOpenChange={setVideo} returnFocusRef={vBtn} />

      {isDesktop ? (
        /* Desktop */
        <div className="mx-auto hidden max-w-[1200px] px-6 pt-32 pb-20 md:px-10 lg:grid lg:grid-cols-[1.05fr_0.95fr] lg:items-center lg:gap-x-16">
          <div className="flex flex-col gap-6">
            <motion.div {...up(0)}><HeroBadge /></motion.div>
            <motion.div {...up(0.05)}><HeroHeadline /></motion.div>
            <motion.div {...up(0.1)}><HeroLead /></motion.div>
            <motion.div {...up(0.15)}><HeroButtons onDemo={() => setVideo(true)} /></motion.div>
            <motion.div {...up(0.2)}><HeroNote /></motion.div>
            <motion.div {...up(0.25)} className="mt-2 max-w-[460px]"><ProofTiles /></motion.div>
          </div>
          <motion.div {...up(0.15)} className="relative flex items-center justify-center self-stretch">
            <HeroStoryPhone testId="hero-story-desktop" />
          </motion.div>
        </div>
      ) : (
        /* Mobile */
        <div className="mx-auto flex max-w-[480px] flex-col gap-4 px-5 pt-28 pb-10 lg:hidden">
          <HeroBadge />
          <HeroHeadline />
          <HeroLead />
          <HeroButtons onDemo={() => setVideo(true)} />
          <HeroNote />
          <div className="mt-3 flex justify-center"><HeroStoryPhone testId="hero-story-mobile" mobile /></div>
          <ProofTiles />
        </div>
      )}
    </section>
  );
};

/* ---------- 2 · SO GEHT'S ---------- */
const SCHRITTE = [
  { n: 1, titel: "Foto schicken", text: "Per WhatsApp an Robbie – wie an einen Kollegen." },
  { n: 2, titel: "Freigeben", text: "Robbie zeigt dir den fertigen Entwurf. Ein Tipp genügt." },
  { n: 3, titel: "Überall sichtbar", text: "Instagram, Google und dein Bildschirm – gleichzeitig." },
];

export const SoGehts = () => (
  <section id="so-gehts" className="scroll-mt-24" style={{ background: CREME }} data-testid="so-gehts">
    <div className="mx-auto max-w-[1180px] px-6 py-10 md:px-10 md:py-24">
      {/* Mobile: Überschrift + einfache Liste + Button */}
      <div className="lg:hidden">
        <h2 style={H} className="text-3xl leading-tight text-[#111]">So einfach geht's.</h2>
        <p style={B} className="mt-3 text-[15px] leading-relaxed text-[#4A4A4A]">Ein Foto reicht – den Rest macht Robbie. Drei Schritte, fertig.</p>
        <div className="mt-5 flex flex-col gap-3">
          {SCHRITTE.map((s) => (
            <div key={s.n} className="flex items-start gap-4 rounded-2xl border-2 border-[#111] bg-white p-5 shadow-[4px_4px_0_0_#111]" data-testid={`so-gehts-${s.n}`}>
              <span style={H} className="grid h-11 w-11 shrink-0 place-items-center rounded-full border-2 border-[#111] bg-[#FFD60A] text-lg text-[#111] shadow-[2px_2px_0_0_#111]">{s.n}</span>
              <div><h3 style={H} className="text-lg text-[#111]">{s.titel}</h3><p style={B} className="mt-1 text-[14px] leading-relaxed text-[#4A4A4A]">{s.text}</p></div>
            </div>
          ))}
        </div>
        <div className="mt-4 flex items-center gap-3 rounded-2xl border-2 border-[#111] bg-[#FFD60A] p-4 shadow-[3px_3px_0_0_#111]" data-testid="so-gehts-kontrolle-mobile">
          <img src={ROBBIE} alt="Robbie" width="44" height="44" loading="lazy" className="w-11 shrink-0" />
          <p style={{ ...B, fontWeight: 600 }} className="text-[14px] leading-snug text-[#111]">Du behältst die Kontrolle: Nichts geht raus, bevor du „Freigeben" tippst.</p>
        </div>
      </div>
      <div className="hidden lg:grid lg:grid-cols-[minmax(300px,1fr)_minmax(0,600px)] lg:gap-14 lg:items-start xl:gap-20">
        <div className="sticky top-28 self-start">
          <h2 style={H} className="text-4xl leading-tight text-[#111]">So einfach <span className="box-decoration-clone bg-[#FFD60A] px-2">geht's.</span></h2>
          <p style={B} className="mt-5 max-w-[42ch] text-[16px] leading-relaxed text-[#4A4A4A]">Ein Foto reicht – den Rest macht Robbie. Keine Technik, keine Vorlagen, kein Zeitfresser. Drei Schritte, und alles ist draußen.</p>
          <Link to="/kunden/registrieren" data-testid="so-gehts-cta" style={{ ...B, fontWeight: 700 }}
            className="mt-7 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full border-2 border-[#111] bg-[#FFD60A] px-6 py-3.5 text-[15px] font-bold text-[#111] shadow-[0_6px_0_0_#111] transition-transform hover:-translate-y-0.5 active:translate-y-0 active:shadow-[0_3px_0_0_#111]">
            30 Tage kostenlos testen <ArrowRight size={18} strokeWidth={2.5} />
          </Link>
          <div className="mt-6 flex items-center gap-3 rounded-2xl border-2 border-[#111] bg-[#FFD60A] p-4 shadow-[3px_3px_0_0_#111] max-w-[42ch]" data-testid="so-gehts-kontrolle">
            <img src={ROBBIE} alt="Robbie" width="52" height="52" loading="lazy" className="w-12 shrink-0" />
            <p style={{ ...B, fontWeight: 600 }} className="text-[14px] leading-snug text-[#111]">Du behältst die Kontrolle: Nichts geht raus, bevor du „Freigeben" tippst.</p>
          </div>
        </div>
        <div className="sogehts-stack relative">
          {SCHRITTE.map((s, i) => (
            <article key={s.n} style={{ "--i": i }}
              className={`sogehts-card ${i === SCHRITTE.length - 1 ? "sogehts-card--last" : ""} ${i > 0 ? "mt-6" : ""} flex items-center gap-6 rounded-[28px] border-2 border-[#111] bg-white p-8 shadow-[8px_8px_0_0_#111]`}
              data-testid={`so-gehts-${s.n}`}>
              <span style={H} className="grid h-16 w-16 shrink-0 place-items-center rounded-full border-2 border-[#111] bg-[#FFD60A] text-2xl text-[#111] shadow-[3px_3px_0_0_#111]">{s.n}</span>
              <div><h3 style={H} className="text-2xl text-[#111]">{s.titel}</h3><p style={B} className="mt-1.5 text-[15px] leading-relaxed text-[#4A4A4A]">{s.text}</p></div>
            </article>
          ))}
        </div>
      </div>
    </div>
  </section>
);

/* ---------- 2a · EINMAL SAGEN. ÜBERALL GESEHEN. (Etappe 4 – nur Inhalt, bestehender Stil) ---------- */
const ES_KARTEN = [
  { icon: Share2, titel: "Überall teilen", text: "Ein Foto an Robbie, ein Klick auf Freigeben: Instagram, Google, dein Bildschirm, deine Mini-Website und das Handy deiner Stammkunden." },
  { icon: MapPin, titel: "Einmal eintragen", text: "Öffnungszeiten, Adresse, Logo einmal pflegen – stimmt überall." },
  { icon: Wallet, titel: "Deine Stammgäste", text: "Wer kommt wieder? Kundenkarte, Tickets und Newsletter auf einen Blick, ein Scanner für alles." },
];
const ES_KLAMMER = [
  { icon: MonitorPlay, titel: "Gesehen", sub: "Social · Google · Website · Bildschirm" },
  { icon: Ticket, titel: "Gekommen", sub: "Aktionen · Tickets" },
  { icon: Wallet, titel: "Wiedergekommen", sub: "Kundenkarte · Newsletter · WhatsApp" },
];

export const EinmalSagen = () => (
  <section id="einmal-sagen" className="scroll-mt-24 bg-white" data-testid="einmal-sagen">
    <div className="mx-auto max-w-[1180px] px-6 py-12 md:px-10 md:py-20">
      <div className="max-w-[46ch]">
        <span style={{ ...B, fontWeight: 700 }} className="inline-flex items-center gap-2 rounded-full border-2 border-[#111] bg-[#FDD105] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.12em] text-[#111]" data-testid="einmal-sagen-eyebrow">Alles verbunden</span>
        <h2 style={H} className="mt-4 text-3xl leading-tight text-[#111] md:text-4xl" data-testid="einmal-sagen-headline">Einmal sagen. <span className="box-decoration-clone bg-[#FFD60A] px-2">Überall gesehen.</span></h2>
      </div>
      <div className="mt-8 grid gap-4 md:grid-cols-3" data-testid="einmal-sagen-karten">
        {ES_KARTEN.map((k) => {
          const Icon = k.icon;
          return (
            <article key={k.titel} className="flex flex-col rounded-2xl border-2 border-[#111] bg-white p-6 shadow-[4px_4px_0_0_#111]" data-testid={`einmal-sagen-karte-${k.titel.split(" ")[0].toLowerCase()}`}>
              <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full border-2 border-[#111] bg-[#FFD60A] shadow-[2px_2px_0_0_#111]" aria-hidden="true"><Icon size={22} strokeWidth={2.2} className="text-[#111]" /></span>
              <h3 style={H} className="mt-4 text-xl text-[#111]">{k.titel}</h3>
              <p style={B} className="mt-2 text-[15px] leading-relaxed text-[#4A4A4A]">{k.text}</p>
            </article>
          );
        })}
      </div>
      <div className="mt-6 rounded-2xl border-2 border-[#111] bg-[#FFF9E6] p-5 shadow-[4px_4px_0_0_#111]" data-testid="einmal-sagen-klammer">
        <div className="grid gap-x-6 gap-y-4 sm:grid-cols-[1fr_auto_1fr_auto_1fr] sm:items-center">
          {ES_KLAMMER.map((g, i) => {
            const Icon = g.icon;
            return (
              <div key={g.titel} className="contents">
                <div className="flex items-start gap-3" data-testid={`einmal-sagen-klammer-${g.titel.toLowerCase()}`}>
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full border-2 border-[#111] bg-white shadow-[2px_2px_0_0_#111]" aria-hidden="true"><Icon size={18} strokeWidth={2.2} className="text-[#111]" /></span>
                  <div className="min-w-0"><p style={{ ...H, fontWeight: 800 }} className="text-[15px] leading-tight text-[#111]">{g.titel}</p><p style={B} className="mt-0.5 text-[13px] leading-snug text-[#4A4A4A]">{g.sub}</p></div>
                </div>
                {i < ES_KLAMMER.length - 1 && <span aria-hidden="true" style={H} className="hidden select-none text-2xl text-[#111]/30 sm:block">·</span>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  </section>
);


/* ---------- 2b · TICKETS & EVENTS ---------- */
export const TicketsEvents = () => {
  const reduce = usePrefersReducedMotion();
  return (
    <section id="tickets-events" className="scroll-mt-24 bg-white" data-testid="tickets-events">
      <div className="mx-auto grid max-w-[1180px] items-center gap-10 px-6 py-12 md:px-10 md:py-20 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16">
        <motion.div {...up(0)}>
          <span style={{ ...B, fontWeight: 700 }} className="inline-flex items-center gap-2 rounded-full border-2 border-[#111] bg-[#FDD105] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.12em] text-[#111]">
            <Ticket size={13} strokeWidth={2.5} aria-hidden="true" /> Neu
          </span>
          <h2 style={H} className="mt-4 text-3xl leading-tight text-[#111] md:text-4xl">Tickets &amp; Events – <span className="box-decoration-clone bg-[#FDD105] px-2">Einlass per QR.</span></h2>
          <p style={B} className="mt-4 max-w-[48ch] text-[15px] leading-relaxed text-[#4A4A4A]">
            Verkauf Tickets für Spieltag, Konzert oder Vereinsfest – ganz ohne Grundgebühr. Deine Gäste zeigen am Eingang einfach den QR-Code, du scannst mit dem Handy. Dauerkarten, Reservierungen und Wallet-Tickets inklusive.
          </p>
          <p style={{ ...B, fontWeight: 600 }} className="mt-3 text-[14px] leading-relaxed text-[#111]">0 € Grundgebühr · ab 0,29 € netto je verkauftem Ticket · persönlich aus Herford betreut.</p>
          <div className="mt-6 flex flex-wrap items-center gap-4">
            <Link to="/tickets" data-testid="tickets-events-cta" style={{ ...B, fontWeight: 700 }}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full border-2 border-[#111] bg-[#FDD105] px-6 py-3.5 text-[15px] font-bold text-[#111] shadow-[0_6px_0_0_#111] transition-transform hover:-translate-y-0.5 active:translate-y-0 active:shadow-[0_3px_0_0_#111]">
              Tickets ansehen <ArrowRight size={18} strokeWidth={2.5} />
            </Link>
            <Link to="/ticketing-sportvereine-owl" data-testid="tickets-events-vereine" style={{ ...B, fontWeight: 600 }}
              className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-[#111] underline decoration-[#FDD105] decoration-2 underline-offset-4 transition-colors hover:decoration-[#111]">
              Für Sportvereine in OWL <ArrowRight size={15} strokeWidth={2.5} />
            </Link>
          </div>
        </motion.div>
        <motion.div {...up(0.1)} className="flex justify-center">
          <div className="relative w-full max-w-[460px] overflow-hidden rounded-[24px] border-2 border-[#111] shadow-[8px_8px_0_0_#111]">
            {reduce ? (
              <img src="/video-lp/robbie-5-poster.jpg" alt="Tickets & Einlass per QR-Code für Events und Vereine" className="aspect-[16/10] w-full object-cover" data-testid="tickets-events-poster" />
            ) : (
              <video src="/video-lp/robbie-5.mp4" poster="/video-lp/robbie-5-poster.jpg" muted autoPlay loop playsInline preload="none"
                className="aspect-[16/10] w-full object-cover" data-testid="tickets-events-video" />
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
};


/* ---------- 3 · BRANCHEN-WEICHE ---------- */
const BRANCHEN = [
  { id: "gastronomie", icon: UtensilsCrossed, titel: "Gastronomie", text: "Tagesgericht fotografieren – Post, Google und Gastraum-Bildschirm sind fertig.", to: "/fallbeispiele/gastronomie" },
  { id: "einzelhandel", icon: Store, titel: "Einzelhandel", text: "Neue Ware einmal fotografieren – Schaufenster-Bildschirm und Instagram laufen mit.", to: "/fallbeispiele/einzelhandel" },
  { id: "verein", icon: Trophy, titel: "Verein", text: "Tickets online verkaufen, Einlass per Handy-QR, Spielbericht nach dem Abpfiff.", to: "/fallbeispiele/sportvereine" },
  { id: "unternehmen", icon: Building2, titel: "Unternehmen", text: "Belege fotografieren – Robbie liest Betrag und Frist und gibt sie an Lexware.", to: "/dein-buero" },
];

export const BranchenWeiche = () => (
  <section id="branchen" className="scroll-mt-24" style={{ background: CREME }} data-testid="branchen-weiche">
    <div className="mx-auto max-w-[1200px] px-6 py-16 md:px-10 md:py-24">
      <motion.h2 {...up(0)} style={H} className="text-[#111] leading-tight text-3xl md:text-4xl">
        Was machst du? <span className="box-decoration-clone bg-[#FFD60A] px-2">Wähl deine Branche.</span>
      </motion.h2>
      <div className="mt-10 grid grid-cols-2 gap-4 md:gap-6 lg:grid-cols-4">
        {BRANCHEN.map((b, i) => {
          const Icon = b.icon;
          return (
            <motion.div key={b.id} {...up(i * 0.07)}>
              <Link to={b.to} data-testid={`branche-${b.id}`}
                className="group flex h-full flex-col rounded-2xl border-2 border-[#111] bg-white p-4 shadow-[4px_4px_0_0_#111] transition-transform duration-200 hover:-translate-y-1 md:p-5">
                <span className="grid h-11 w-11 place-items-center rounded-xl bg-[#111] text-[#FFD60A] transition-colors group-hover:bg-[#FFD60A] group-hover:text-[#111]"><Icon size={22} strokeWidth={2} aria-hidden="true" /></span>
                <h3 style={H} className="mt-3 text-lg text-[#111]">{b.titel}</h3>
                <p style={B} className="mt-1.5 flex-1 text-[13px] leading-relaxed text-[#4A4A4A]">{b.text}</p>
                <span style={{ ...B, fontWeight: 700 }} className="mt-3 inline-flex items-center gap-1.5 text-[13px] font-bold text-[#111] underline decoration-[#FFD60A] decoration-[3px] underline-offset-4">
                  Beispiel ansehen <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
                </span>
              </Link>
            </motion.div>
          );
        })}
      </div>
    </div>
  </section>
);

/* ---------- 2b · DAS KANN ROBBIE (4 Säulen, kompakt) ---------- */
const SAEULEN = [
  { id: "tickets", icon: Ticket, titel: "Ticketsystem", text: "Tickets verkaufen, reservieren und Einlass per QR – alles aus einer Hand.", to: "/tickets", farbe: "#FE2C55" },
  { id: "kundenkarte", icon: Wallet, titel: "Kundenkarte", text: "Digitaler Sammelpass in Apple & Google Wallet – mit Scanner für dein Team.", to: "/kundenkarte", farbe: "#34A853" },
  { id: "social", icon: Share2, titel: "Social Media", text: "Foto schicken, Robbie postet auf Instagram, Facebook & Google – per WhatsApp.", to: "/social", farbe: "#1877F2" },
  { id: "tiktok", icon: Music2, titel: "TikTok-Schnitt", text: "Video hochladen oder schicken – fertiges Hochformat-Reel mit Schnitt & Untertiteln.", to: "/tiktok", farbe: "#111", neu: true },
];

export const DasKannRobbie = () => (
  <section id="das-kann-robbie" className="scroll-mt-24 bg-white" data-testid="das-kann-robbie">
    <div className="mx-auto max-w-[1200px] px-6 py-16 md:px-10 md:py-24">
      <motion.div {...up(0)} className="max-w-[680px]">
        <span style={{ ...B, fontWeight: 700 }} className="inline-block rounded-full border-2 border-[#111] bg-[#FFD60A] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.12em] text-[#111]">Das kann Robbie</span>
        <h2 style={H} className="mt-4 text-[#111] leading-tight text-3xl md:text-4xl">Mehr als Social Media. <span className="box-decoration-clone bg-[#FFD60A] px-2">Vier starke Werkzeuge.</span></h2>
        <p style={B} className="mt-3 text-[15px] leading-relaxed text-[#4A4A4A]">Ticketsystem, digitale Kundenkarte, Social Media und TikTok-Schnitt – in jedem Paket enthalten, bedient per WhatsApp.</p>
      </motion.div>
      <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-6 lg:grid-cols-4">
        {SAEULEN.map((s, i) => {
          const Icon = s.icon;
          return (
            <motion.div key={s.id} {...up(i * 0.07)}>
              <Link to={s.to} data-testid={`saeule-${s.id}`}
                className="group flex h-full flex-col rounded-2xl border-2 border-[#111] bg-white p-5 shadow-[4px_4px_0_0_#111] transition-transform duration-200 hover:-translate-y-1">
                <span className="flex items-center gap-2">
                  <span className="grid h-11 w-11 place-items-center rounded-xl text-white transition-transform group-hover:scale-105" style={{ background: s.farbe }}><Icon size={22} strokeWidth={2} aria-hidden="true" /></span>
                  {s.neu && <span style={{ ...B, fontWeight: 700 }} className="rounded-full bg-[#FFD60A] px-1.5 py-px text-[9px] font-bold tracking-[0.08em] text-[#111]">NEU</span>}
                </span>
                <h3 style={H} className="mt-3 text-lg text-[#111]">{s.titel}</h3>
                <p style={B} className="mt-1.5 flex-1 text-[13px] leading-relaxed text-[#4A4A4A]">{s.text}</p>
                {s.id === "social" && <KanalLogoRow className="mt-3" />}
                <span style={{ ...B, fontWeight: 700 }} className="mt-3 inline-flex items-center gap-1.5 text-[13px] font-bold text-[#111] underline decoration-[#FFD60A] decoration-[3px] underline-offset-4">
                  Mehr erfahren <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
                </span>
              </Link>
            </motion.div>
          );
        })}
      </div>
    </div>
  </section>
);

/* ---------- 3b · ALLES DRIN (Leistungen) ---------- */
const WalletBadge = () => (
  <span style={{ ...B, fontWeight: 700 }} className="inline-flex items-center gap-1 rounded-full bg-[#111] px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.04em] text-[#FFD60A]" data-testid="leistung-wallet-badge">
    <Wallet size={10} strokeWidth={2.5} aria-hidden="true" /> Apple &amp; Google Wallet
  </span>
);

const LEISTUNGSGRUPPEN = [
  { id: "social", titel: "Social & Video", items: [
    { id: "social", icon: Share2, name: "Social Media", text: "Posten lassen – per WhatsApp.", to: "/social" },
    { id: "tiktok", icon: Music2, name: "TikTok", text: "Kurze Videos fürs Geschäft.", to: "/tiktok", neu: true },
    { id: "video", icon: Clapperboard, name: "Videostudio", text: "Schneiden, Untertitel, Musik.", to: "/videostudio" },
    { id: "grafik", icon: Palette, name: "Grafik-Studio", text: "Druckreife Grafiken & Logo.", to: "/grafik-studio" },
  ] },
  { id: "gefunden", titel: "Online gefunden werden", items: [
    { id: "google", icon: MapPin, name: "Google-Profil", text: "Beiträge, Bewertungen, Fotos.", to: "/google" },
    { id: "website", icon: Globe, name: "Website", text: "Baukasten – in Minuten online.", to: "/homepage" },
    { id: "miniwebsite", icon: Smartphone, name: "Mini-Website", text: "Links, Galerie & Öffnungszeiten.", to: "/mini-website", neu: true },
    { id: "bildschirme", icon: MonitorPlay, name: "Digitale Bildschirme", text: "Schaufenster, Gastraum & Halle.", to: "/digital-signage" },
  ] },
  { id: "kunden", titel: "Kunden & Events", items: [
    { id: "kundenkarte", icon: Wallet, name: "Kundenkarte", text: "Digitaler Sammelpass & Scanner.", to: "/kundenkarte", wallet: true },
    { id: "ticketing", icon: Ticket, name: "E-Ticketing", text: "Verkauf, Reservierung, Einlass per QR.", to: "/tickets", wallet: true },
    { id: "newsletter", icon: Mail, name: "Newsletter", text: "Mails im eigenen Design, mit Anmeldung.", to: "/fallbeispiele/newsletter" },
  ] },
  { id: "buero", titel: "Büro & Dateien", items: [
    { id: "buero", icon: Receipt, name: "Büro & Belege", text: "Belege, E-Mails, Termine vorsortiert.", to: "/dein-buero" },
    { id: "cloud", icon: Cloud, name: "Cloud", text: "Dateien hochladen und teilen.", to: "/cloud" },
    { id: "hosting", icon: Server, name: "Hosting", text: "WordPress-Hosting, Domains, Backups.", to: "/hosting" },
  ] },
];

const LeistungItem = ({ it, gid }) => {
  const Icon = it.icon;
  return (
    <Link to={it.to} data-testid={`leistung-${gid}-${it.id}`}
      className="group flex items-start gap-3 rounded-xl border-2 border-[#111] bg-white p-3 shadow-[2px_2px_0_0_#111] transition-transform duration-200 hover:-translate-y-0.5">
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-[#111] text-[#FFD60A] transition-colors group-hover:bg-[#FFD60A] group-hover:text-[#111]"><Icon size={18} strokeWidth={2} aria-hidden="true" /></span>
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
          <span style={{ ...H, fontWeight: 700 }} className="text-[14px] text-[#111]">{it.name}</span>
          {it.wallet && <WalletBadge />}
          {it.neu && <span style={{ ...B, fontWeight: 700 }} className="rounded-full bg-[#FFD60A] px-1.5 py-px text-[9px] font-bold tracking-[0.08em] text-[#111]">NEU</span>}
        </span>
        <span style={B} className="mt-0.5 block text-[12px] leading-snug text-[#6B6760]">{it.text}</span>
      </span>
      <ArrowRight size={15} className="mt-1 shrink-0 text-[#111]/40 transition-transform duration-300 group-hover:translate-x-1 group-hover:text-[#111]" aria-hidden="true" />
    </Link>
  );
};

export const LeistungenBlock = () => {
  const isDesktop = useIsDesktop();
  const revealRef = useRef(null);
  useRevealFallback(revealRef);
  return (
    <section id="leistungen" ref={revealRef} className="scroll-mt-24 bg-white" data-testid="leistungen-block">
      <div className="mx-auto max-w-[1200px] px-6 py-16 md:px-10 md:py-24">
        <motion.div {...up(0)} className="max-w-[680px]">
          <span style={{ ...B, fontWeight: 700 }} className="inline-block rounded-full border-2 border-[#111] bg-[#FFD60A] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.12em] text-[#111]">Alles drin – in jedem Paket</span>
          <h2 style={H} className="mt-4 text-[#111] leading-tight text-3xl md:text-4xl">Ein Login. <span className="box-decoration-clone bg-[#FFD60A] px-2">Alle Werkzeuge.</span></h2>
          <p style={B} className="mt-3 text-[15px] leading-relaxed text-[#4A4A4A]">Kein Baukasten zum Zusammenstückeln: Social Media, Google, Website, Bildschirme, Tickets, Kundenkarte, Newsletter, Cloud und Büro – alles in einem Paket, bedient per WhatsApp.</p>
        </motion.div>

        {isDesktop ? (
          <div className="mt-10 grid grid-cols-2 gap-4 md:gap-6 lg:grid-cols-4" data-testid="leistungen-desktop">
            {LEISTUNGSGRUPPEN.map((g, gi) => {
              const base = LEISTUNGSGRUPPEN.slice(0, gi).reduce((a, x) => a + x.items.length, 0);
              return (
                <div key={g.id} className="flex flex-col" data-testid={`leistung-gruppe-${g.id}`}>
                  <div className="mb-3 flex items-center gap-2 border-b-2 border-[#111] pb-2">
                    <h3 style={H} className="text-lg text-[#111]">{g.titel}</h3>
                    <span style={{ ...B, fontWeight: 700 }} className="ml-auto rounded-full bg-[#111] px-2 py-0.5 text-[10px] font-bold text-white">{g.items.length}</span>
                  </div>
                  <div className="flex flex-col gap-2.5">
                    {g.items.map((it, ii) => (
                      <div key={it.id} className="sd-reveal" style={{ "--sd-i": base + ii }}>
                        <LeistungItem it={it} gid={g.id} />
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="mt-8 flex flex-col gap-3" data-testid="leistungen-mobile">
            {LEISTUNGSGRUPPEN.map((g, gi) => (
              <details key={g.id} open={gi === 0} className="group overflow-hidden rounded-2xl border-2 border-[#111] bg-white shadow-[3px_3px_0_0_#111]" data-testid={`leistung-gruppe-${g.id}`}>
                <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3.5" data-testid={`leistung-gruppe-${g.id}-toggle`}>
                  <span className="flex items-center gap-2">
                    <span style={H} className="text-[16px] text-[#111]">{g.titel}</span>
                    <span style={{ ...B, fontWeight: 700 }} className="rounded-full bg-[#111] px-2 py-0.5 text-[10px] font-bold text-white">{g.items.length}</span>
                  </span>
                  <ChevronDown size={20} className="shrink-0 text-[#111] transition-transform duration-300 group-open:rotate-180" aria-hidden="true" />
                </summary>
                <div className="flex flex-col gap-2.5 border-t-2 border-[#111]/10 px-3 pb-3 pt-3">
                  {g.items.map((it) => <LeistungItem key={it.id} it={it} gid={g.id} />)}
                </div>
              </details>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};


/* ---------- 4 · PREIS-BALKEN ---------- */
export const PreisBalken = () => (
  <section id="preise" className="scroll-mt-24" style={{ background: CREME }} data-testid="preis-balken">
    <div className="mx-auto max-w-[1200px] px-6 pb-16 md:px-10 md:pb-24">
      <motion.div {...up(0)} className="rounded-[28px] bg-[#111] px-6 py-10 text-white md:px-12 md:py-12">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="max-w-[640px]">
            <p style={{ ...B, fontWeight: 700 }} className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#FFD60A]">Alles drin – ein Preis</p>
            <p style={H} className="mt-2 text-3xl md:text-4xl">ab 59 € im Monat <span className="text-white/60">– weniger als 2 € am Tag.</span></p>
            <p style={B} className="mt-3 text-[14px] leading-relaxed text-white/70">Social Media, Google, Bildschirme, Website, Tickets, Kundenkarte und Büro. Netto zzgl. MwSt., monatlich kündbar.</p>
          </div>
          <Link to="/kunden/registrieren" data-testid="preis-cta" style={{ ...B, fontWeight: 700 }}
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full border-2 border-[#FFD60A] bg-[#FFD60A] px-6 py-3.5 text-[15px] font-bold text-[#111] shadow-[0_6px_0_0_rgba(255,214,10,0.35)] transition-transform hover:-translate-y-0.5">
            30 Tage kostenlos testen <ArrowRight size={18} strokeWidth={2.5} />
          </Link>
        </div>
      </motion.div>
    </div>
  </section>
);
